import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Sparkles } from "lucide-react";

interface HeroProps {
  onContactClick?: () => void;
}

const Hero = ({ onContactClick }: HeroProps) => {
  const features = [
    "IA Avançada",
    "App Mobile PWA",
    "Setup em 5min"
  ];

  return (
    <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden">
      <div className="absolute inset-0 grid-pattern" />
      
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="space-y-5 md:space-y-8 text-center lg:text-left">
            {/* Badge - no delay for faster LCP */}
            <div className="inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium">
              <Sparkles className="w-3.5 h-3.5 md:w-4 md:h-4" />
              Nutrição do Futuro
            </div>
            
            {/* H1 - critical for LCP, font is preloaded */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-display leading-tight">
              Pare de Perder{" "}
              <span className="text-gradient">5 Horas por Semana</span>{" "}
              com Planos Alimentares
            </h1>
            
            {/* Subheadline - LCP element, render immediately */}
            <p className="text-base md:text-lg text-muted-foreground max-w-lg mx-auto lg:mx-0">
              Enquanto você gasta <strong className="text-foreground">2 horas montando um plano</strong>, nutricionistas com IA fazem em <strong className="text-foreground">30 segundos</strong>. Seus concorrentes já estão usando.
            </p>
            
            {/* CTAs - render immediately for LCP optimization */}
            <div className="flex flex-wrap gap-3 md:gap-4 justify-center lg:justify-start">
              <Button size="lg" className="text-sm md:text-base" onClick={onContactClick}>
                Entre em contato
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="lg" className="text-sm md:text-base" onClick={onContactClick}>
                Ver Demonstração
              </Button>
            </div>
            
            {/* Features - render immediately for LCP optimization */}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 md:gap-6 pt-2 md:pt-4">
              {features.map((feature) => (
                <div key={feature} className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-success/20 flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-success" />
                  </div>
                  <span className="text-xs md:text-sm text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            {/* Disable float animation on mobile for better performance */}
            <div className="relative md:animate-float">
              {/* Browser mockup */}
              <div className="bg-card rounded-2xl shadow-[var(--shadow-elevated)] border border-border/50 p-4 transform rotate-[-2deg]">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-destructive/60" />
                  <div className="w-3 h-3 rounded-full bg-success/60" />
                  <div className="w-3 h-3 rounded-full bg-muted" />
                  <div className="flex-1 h-6 bg-muted rounded-md ml-4" />
                </div>
                <div className="space-y-3">
                  <div className="h-8 bg-muted rounded-md w-3/4" />
                  <div className="grid grid-cols-3 gap-3">
                    <div className="h-20 bg-accent rounded-lg" />
                    <div className="h-20 bg-primary/20 rounded-lg" />
                    <div className="h-20 bg-accent rounded-lg" />
                  </div>
                  <div className="h-24 bg-muted/50 rounded-lg" />
                </div>
              </div>
              
              {/* iPhone mockup */}
              <div className="absolute -right-4 top-1/4 w-44 transform rotate-[5deg]">
                {/* iPhone frame */}
                <div className="bg-[#1a1a1a] rounded-[2.5rem] p-[3px] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.1)]">
                  {/* Side buttons */}
                  <div className="absolute -left-[2px] top-24 w-[3px] h-8 bg-[#2a2a2a] rounded-l-sm" />
                  <div className="absolute -left-[2px] top-36 w-[3px] h-12 bg-[#2a2a2a] rounded-l-sm" />
                  <div className="absolute -left-[2px] top-[200px] w-[3px] h-12 bg-[#2a2a2a] rounded-l-sm" />
                  <div className="absolute -right-[2px] top-28 w-[3px] h-16 bg-[#2a2a2a] rounded-r-sm" />
                  
                  {/* Screen area */}
                  <div className="bg-card rounded-[2.3rem] overflow-hidden">
                    {/* Dynamic Island */}
                    <div className="flex justify-center pt-3 pb-2 bg-card">
                      <div className="w-24 h-7 bg-[#1a1a1a] rounded-full flex items-center justify-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#2a2a2a]" />
                        <div className="w-3 h-3 rounded-full bg-[#0a0a0a] ring-1 ring-[#2a2a2a]" />
                      </div>
                    </div>
                    
                    {/* Screen content */}
                    <div className="px-4 pb-4 space-y-3">
                      {/* App header */}
                      <div className="flex items-center justify-between">
                        <div className="h-5 bg-muted rounded-md w-20" />
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-primary" />
                        </div>
                      </div>
                      
                      {/* Card */}
                      <div className="bg-primary/10 rounded-xl p-3 space-y-2">
                        <div className="h-4 bg-primary/30 rounded w-3/4" />
                        <div className="h-3 bg-primary/20 rounded w-1/2" />
                      </div>
                      
                      {/* Stats row */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-muted/50 rounded-lg p-2 text-center">
                          <div className="h-5 bg-success/30 rounded w-full mb-1" />
                          <div className="h-3 bg-muted rounded w-2/3 mx-auto" />
                        </div>
                        <div className="bg-muted/50 rounded-lg p-2 text-center">
                          <div className="h-5 bg-primary/30 rounded w-full mb-1" />
                          <div className="h-3 bg-muted rounded w-2/3 mx-auto" />
                        </div>
                      </div>
                      
                      {/* List item */}
                      <div className="flex items-center gap-2 bg-muted/30 rounded-lg p-2">
                        <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                          <Check className="w-5 h-5 text-success" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="h-3 bg-muted rounded w-3/4" />
                          <div className="h-2 bg-muted/50 rounded w-1/2" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Home indicator */}
                    <div className="flex justify-center pb-2 pt-1">
                      <div className="w-28 h-1 bg-foreground/20 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
